import { STATUS_UI } from "./constants";

export type Option = { id: string; label: string };

export type UploadSlot = {
  fileName: string;
  fileExtension: string;
  key: string;
  isUploading: boolean;
};

export const emptySlot = (): UploadSlot => ({
  fileName: "",
  fileExtension: "",
  key: "",
  isUploading: false,
});

export type UploadedDoc = {
  fileName: string;
  fileExtension: string;
  key: string;
  fileSize?: number;
};

const pad = (n: number) => String(n).padStart(2, "0");

export function getSessionUser(): any {
  try {
    const raw = localStorage.getItem("user");
    if (!raw || raw === "undefined") return {};
    const parsed = JSON.parse(raw);
    return parsed?.data?.user || parsed?.user || parsed?.data || parsed || {};
  } catch {
    return {};
  }
}

export function getFamilyMembers(user: any): any[] {
  const candidates = [user?.familyMembers, user?.details?.familyMembers, user?.data?.familyMembers];
  for (const c of candidates) if (Array.isArray(c)) return c;
  return [];
}

export function employeeProfile(user: any) {
  const details = user?.details || {};
  const name = `${details.firstname ?? ""} ${details.lastname ?? details.lsastname ?? ""}`.trim();
  const members = getFamilyMembers(user);
  const spouse = members.find((m: any) => String(m?.relation ?? "").toLowerCase() === "spouse");
  const spouseName = spouse ? `${spouse.firstname ?? ""} ${spouse.lastname ?? ""}`.trim() : "";
  return {
    employeeNo: String(details.empId ?? user?.empId ?? ""),
    employeeName: name,
    designation: String(
      details.sap_department_designation_wings?.designation_name ?? details.designation ?? ""
    ),
    payScale: String(details.pay_scale ?? details.payscale ?? ""),
    basicPay: String(details.basicSalary ?? details.basicPay ?? details.basic_pay ?? ""),
    contactNo: String(details.phone ?? details.mobile ?? ""),
    spouseName,
    spouseMember: spouse,
  };
}

export function normalizeMasterOptions(list: any): Option[] {
  const arr = Array.isArray(list) ? list : [];
  return arr
    .map((item: any) => ({
      id: String(item?.Value ?? item?.value ?? item?.code ?? item?.Code ?? item?.id ?? ""),
      label: String(
        item?.Ddtext ?? item?.ddtext ?? item?.name ?? item?.Name ?? item?.label ?? item?.text ?? ""
      ),
    }))
    .filter((o) => o.label !== "");
}

/** Last/Latest Approver as "NAME (ApproverUserID)". */
export function formatApprover(name?: string, id?: string): string {
  const n = String(name ?? "").trim();
  const i = String(id ?? "").trim();
  const showId = i !== "" && !/^0+$/.test(i);
  if (n && showId) return `${n} (${i})`;
  return n || (showId ? i : "-");
}

export function listFromResponse(res: any): any[] {
  const list = res?.data?.data ?? res?.data ?? [];
  return Array.isArray(list) ? list : [];
}

export function detailFromResponse(res: any): any {
  const data = res?.data?.data ?? res?.data ?? null;
  if (data && typeof data === "object" && !Array.isArray(data)) return data;
  return null;
}

export function fromSapDate(raw?: string | null): string {
  if (!raw) return "";
  const str = String(raw).trim();
  const dmy = str.match(/^(\d{2})[-.](\d{2})[-.](\d{4})$/);
  if (dmy) {
    return dmy[1] === "00" || dmy[2] === "00" || dmy[3] === "0000" ? "" : `${dmy[1]}.${dmy[2]}.${dmy[3]}`;
  }
  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return iso[1] === "9999" || iso[1] === "0000" ? "" : `${iso[3]}.${iso[2]}.${iso[1]}`;
  }
  const match = str.match(/\/Date\((-?\d+)\)\//);
  const ms = match ? Number(match[1]) : NaN;
  if (isNaN(ms) || ms >= 253402214400000) return "";
  const d = new Date(ms);
  return `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`;
}

export function fromSapDateTime(raw?: string | null): string {
  const str = String(raw ?? "").trim();
  const odata = str.match(/\/Date\((-?\d+)\)\//);
  const hasZone = /T.*(Z|[+-]\d{2}:?\d{2})$/i.test(str);
  const ms = odata ? Number(odata[1]) : hasZone ? Date.parse(str) : NaN;
  if (!Number.isFinite(ms) || ms >= 253402214400000) return fromSapDate(str) || str;
  const d = new Date(ms);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function parseDMY(str?: string): Date | undefined {
  const m = String(str ?? "").match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!m) return undefined;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return isNaN(d.getTime()) ? undefined : d;
}

export function toISODate(ddmmyyyy: string): string {
  const d = parseDMY(ddmmyyyy);
  return d ? d.toISOString() : "";
}

export function dmyToYmd(dmy?: string): string {
  const m = String(dmy ?? "").match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
}

export function ymdToDmy(ymd: string): string {
  const m = String(ymd ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  return `${m[3]}.${m[2]}.${m[1]}`;
}

export function formatDob(raw?: string | null): string {
  if (!raw) return "-";
  const s = String(raw).trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/) || s.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (m) return m[1] === "0000" ? "-" : `${m[3]}.${m[2]}.${m[1]}`;
  m = s.match(/^(\d{2})[.\-/](\d{2})[.\-/](\d{4})$/);
  if (m) return `${m[1]}.${m[2]}.${m[3]}`;
  m = s.match(/\/Date\((-?\d+)\)\//);
  if (m) {
    const d = new Date(Number(m[1]));
    return isNaN(d.getTime())
      ? "-"
      : `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`;
  }
  return "-";
}

export function toSapAmount(v: string) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toFixed(3) : "0.000";
}

export function formatAmount(v: any): string {
  const n = parseFloat(String(v ?? ""));
  return Number.isFinite(n) ? n.toFixed(2) : "-";
}

export function unwrapResults(v: any): any[] {
  return Array.isArray(v) ? v : Array.isArray(v?.results) ? v.results : [];
}

export function pick(raw: any, keys: string[]): string {
  for (const k of keys) {
    const v = raw?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return String(v);
  }
  return "";
}

export function yesNo(v: any): string {
  const s = String(v ?? "").trim().toUpperCase();
  if (s === "Y" || s === "YES" || s === "X") return "Yes";
  if (s === "N" || s === "NO") return "No";
  return s || "-";
}

export function yesNoNa(v: any): string {
  return String(v ?? "").trim() === "" ? "Not Applicable" : yesNo(v);
}

export function nonZeroId(v: any): string {
  const str = String(v ?? "").trim();
  return /^0*$/.test(str) ? "" : str;
}

export function phoneOk(value: string) {
  return /^\+?\d{10,15}$/.test(value.trim());
}

export function getOccupationDetailField(occupationLabel: string, isDeceasedContext = false) {
  const label = occupationLabel.toLowerCase().trim();
  if (!label || label.includes("not employed")) return { visible: false, label: "Details of employer" };
  if (label.includes("self")) return { visible: true, label: "Please mention full details of profession" };
  if (label === "others" || label === "other") {
    return { visible: true, label: isDeceasedContext ? "Please Specify Occupation" : "Mention source of income" };
  }
  return { visible: true, label: "Details of employer" };
}

export function getOccupationCategory(label: string): "dmrc" | "govt_psu" | "other" {
  const l = label.toLowerCase();
  if (l.includes("dmrc")) return "dmrc";
  if (l.includes("govt") || l.includes("psu")) return "govt_psu";
  return "other";
}

export function getEmploymentCategory(label: string): "dmrc" | "govt" | "cpse_psu" | "private" | "other" {
  const l = label.toLowerCase().trim();
  if (l.includes("dmrc")) return "dmrc";
  if (l.includes("private")) return "private";
  if (l.includes("central") && l.includes("govt")) return "govt";
  if ((l.includes("cpse") || l.includes("psu")) && !l.includes("retired")) return "cpse_psu";
  return "other";
}

export function statusUi(status?: string) {
  const rawUpper = String(status || "").trim().toUpperCase().replace(/[\s-]+/g, "_");
  let ui = STATUS_UI[rawUpper];
  if (!ui) {
    if (rawUpper.includes("APPROV")) ui = STATUS_UI.APPROVED;
    else if (rawUpper.includes("REJECT")) ui = STATUS_UI.REJECTED;
    else if (rawUpper.includes("PROCESS") || rawUpper.includes("PENDING")) ui = STATUS_UI.TO_BE_APPROVED;
    else if (rawUpper.includes("DRAFT")) ui = STATUS_UI.DRAFT;
    else if (rawUpper) ui = STATUS_UI.DEFAULT;
  }
  return ui || null;
}

export function isRejectedStatus(status: string) {
  const s = status.trim().toUpperCase();
  return s === "R" || s.includes("REJECT");
}

export function fileExt(name: string) {
  return (name.split(".").pop() || "").toLowerCase();
}

export function isJpeg(file: File) {
  const ext = fileExt(file.name);
  const mime = (file.type || "").toLowerCase();
  return ext === "jpg" || ext === "jpeg" || mime === "image/jpeg" || mime === "image/jpg";
}

export function isPng(file: File) {
  const ext = fileExt(file.name);
  return ext === "png" || (file.type || "").toLowerCase() === "image/png";
}

export function isPdf(file: File) {
  const ext = fileExt(file.name);
  return ext === "pdf" || (file.type || "").toLowerCase() === "application/pdf";
}

export function isSupportedDoc(file: File) {
  return isPdf(file) || isJpeg(file) || isPng(file);
}

export function isFamilyPhoto(file: File) {
  const ext = fileExt(file.name);
  return ext === "png" || ext === "jpg" || ext === "jpeg";
}

export function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const raw = result.includes(",") ? result.split(",")[1] : result;
      resolve(raw);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function labelOf(options: Option[], code: any): string {
  if (code === undefined || code === null) return "-";
  const found = options.find((o) => o.id === String(code));
  return found?.label || String(code).trim() || "-";
}

export type InclusionKind = "parents" | "family" | "delete";

export function detectInclusionKind(task: any): InclusionKind | null {
  const type = String(task?.type || task?.originalItem?.type || "")
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (type === "INC_PARENTS" || type.includes("INC_PARENT")) return "parents";
  if (type === "INC_FAMILY" || type.includes("INC_FAMILY")) return "family";
  if (type === "INC_DELETE" || type.includes("INC_DELETE") || type.includes("DEPENDENT_DELETION")) return "delete";
  const text = `${task?.taskText || ""} ${task?.workItemText || ""} ${task?.subject || ""}`.toLowerCase();
  const family =
    text.includes("family member") ||
    text.includes("spouse and child") ||
    text.includes("spouse/child") ||
    (text.includes("spouse") && text.includes("child"));
  const parents = text.includes("dependent parent") || text.includes("widow mother");
  if (family && !parents) return "family";
  if (parents && (text.includes("delet") || text.includes("inc_delete"))) return "delete";
  if (parents && (text.includes("inclus") || text.includes("addition"))) return "parents";
  if (parents && text.includes("delet")) return "delete";
  return null;
}
